/* Your computers + pairing (not in the handoff — built from the kit). */
import { useCallback, useEffect, useRef, useState } from 'react';
import { browserOs, claimComputer, helperDownloadUrl, listDevices, pairComputer, updateDevice } from '../../api/bridge.js';
import { useLocalHelper } from '../../api/localHelper.js';
import { Button, Card, EmptyState, IconTile, Input, Modal, Skeleton, Tag } from '../../ui/index.jsx';
import './computers.css';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/index.js';

/** The Helper on this computer (local check), claimed for the signed-in user when it is not on the account yet (Helper 0.1.9+:
 * one install serves whoever is signed in on that computer). Returns the local-check state plus `claimed` (just linked). */
export function useLocalComputer(devices, refresh, intervalMs = 15000) {
  const lh = useLocalHelper(intervalMs);
  const [claimed, setClaimed] = useState(null);
  const tried = useRef('');
  useEffect(() => {
    const l = lh.local;
    if (!l || !l.deviceId || !l.claim || !devices) return;
    if (devices.some((d) => d.id === l.deviceId)) return;
    const key = `${l.deviceId}:${l.claim}`;
    if (tried.current === key) return;
    tried.current = key;
    claimComputer(l.deviceId, l.claim).then((r) => { if (r.ok) { setClaimed(r.data.device); refresh && refresh(); } });
  }, [lh.local, devices, refresh]);
  return { ...lh, claimed };
}

/** The user's computers, refreshed every 15 s. */
export function useDevices(intervalMs = 15000) {
  const [devices, setDevices] = useState(null);
  const [error, setError] = useState(null);
  const refresh = useCallback(async () => {
    const r = await listDevices();
    if (r.ok) { setDevices(r.data.devices || []); setError(null); } else setError(r.error);
  }, []);
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, intervalMs);
    return () => clearInterval(t);
  }, [refresh, intervalMs]);
  return { devices, error, refresh };
}

const ago = (iso) => {
  if (!iso) return 'never';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return i18n.t('bv:time.justNowLower');
  if (s < 3600) return i18n.t('bv:time.minAgo', { count: Math.round(s / 60) });
  if (s < 86400) return i18n.t('bv:time.hoursAgo', { count: Math.round(s / 3600) });
  return new Date(iso).toLocaleDateString(i18n.language);
};
const osName = (os) => (os === 'darwin' ? 'Mac' : os === 'win32' ? 'Windows' : os || i18n.t('bv:print.computer'));

function ComputerCard({ d, onChanged }) {
  const { t } = useTranslation('bv');
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(d.name || '');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => { setBusy(true); await updateDevice(d.id, { name }); setBusy(false); setRenaming(false); onChanged(); };
  const signOut = async () => { setBusy(true); await updateDevice(d.id, { revoke: true }); setBusy(false); setConfirm(false); onChanged(); };
  const printers = Array.isArray(d.printerList) ? d.printerList.length : 0;
  return (
    <Card className="bv-computer" data-testid="computer-card">
      <IconTile icon="print" color={d.online ? 'green' : 'white'} />
      <div className="bv-computer__main">
        {renaming ? (
          <div className="bv-computer__rename">
            <Input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} aria-label={t('computers.name')} autoFocus />
            <Button variant="green" size="sm" icon="check" disabled={busy || !name.trim()} onClick={save}>{t('common.save')}</Button>
            <Button size="sm" onClick={() => { setName(d.name || ''); setRenaming(false); }}>{t('common.cancel')}</Button>
          </div>
        ) : (
          <div className="bv-computer__name">{d.name}</div>
        )}
        <div className="bv-computer__meta">
          {d.online ? <Tag color="green">{t('computers.online')}</Tag> : <Tag color="yellow">{t('computers.offline')}</Tag>}
          <span>{osName(d.os)}</span>
          <span>{t('computers.printers', { count: printers })}</span>
          <span>Helper {d.engineVersion || '–'}</span>
          <span>{d.online ? t('computers.connectedNow') : t('computers.lastSeen', { when: ago(d.lastSeen) })}</span>
        </div>
      </div>
      {!renaming && (
        <div className="bv-computer__actions">
          <Button size="sm" onClick={() => setRenaming(true)}>{t('computers.rename')}</Button>
          <Button size="sm" onClick={() => setConfirm(true)}>{t('shell.signOut')}</Button>
        </div>
      )}
      <Modal open={confirm} title={t('computers.signOut.title')} onClose={() => setConfirm(false)}>
        <p className="bv-muted">{t('computers.signOut.body', { name: d.name })}</p>
        <div className="bv-modal-actions">
          <Button onClick={() => setConfirm(false)}>{t('common.cancel')}</Button>
          <Button variant="coral" disabled={busy} onClick={signOut}>{t('computers.signOut.confirm')}</Button>
        </div>
      </Modal>
    </Card>
  );
}

/** Step 1 download, step 2 enter the code the Helper shows. */
export function ConnectComputer({ onPaired, initialCode = '' }) {
  const { t } = useTranslation('bv');
  const [code, setCode] = useState(initialCode);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const os = browserOs();
  const pair = async (e) => {
    e && e.preventDefault();
    const c = code.trim().toUpperCase();
    if (!/^[A-Z0-9]{6}$/.test(c)) { setMsg({ err: true, text: t('computers.connect.badCode') }); return; }
    setBusy(true); setMsg(null);
    const r = await pairComputer(c);
    setBusy(false);
    if (!r.ok) { setMsg({ err: true, text: r.error }); return; }
    setMsg({ ok: true, text: t('computers.connect.connected', { name: r.data.name }) });
    setCode('');
    onPaired && onPaired(r.data);
  };
  return (
    <Card className="bv-connect" data-testid="connect-computer">
      <h2 className="bv-connect__title">{t('print.noComputer.action')}</h2>
      <p className="bv-muted">{t('computers.connect.intro')}</p>
      <div className="bv-connect__steps">
        <div className="bv-connect__step">
          <span className="bv-connect__num">1</span>
          <div>
            <div className="bv-connect__steptitle">{t('computers.connect.step1')}</div>
            <p className="bv-small bv-muted">{t('computers.connect.step1Hint')}</p>
            <div className="bv-connect__downloads">
              <Button as="a" href={helperDownloadUrl(os)} variant="blue" icon="upload">{t('computers.connect.downloadFor', { os: os === 'mac' ? 'Mac' : 'Windows' })}</Button>
              <a href={helperDownloadUrl(os === 'mac' ? 'win' : 'mac')}>{t('computers.connect.otherVersion', { os: os === 'mac' ? 'Windows' : 'Mac' })}</a>
            </div>
          </div>
        </div>
        <div className="bv-connect__step">
          <span className="bv-connect__num">2</span>
          <div style={{ flex: 1 }}>
            <div className="bv-connect__steptitle">{t('computers.connect.step2')}</div>
            <form className="bv-connect__form" onSubmit={pair} noValidate>
              <Input className="bv-connect__code" value={code} maxLength={6} placeholder="ABC123" aria-label={t('computers.connect.codeAria')}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))} />
              <Button type="submit" variant="green" icon="link" disabled={busy}>{busy ? t('computers.connect.connecting') : t('computers.connect.connect')}</Button>
            </form>
            {msg && <p className={`bv-connect__msg ${msg.err ? 'is-err' : 'is-ok'}`} role={msg.err ? 'alert' : 'status'}>{msg.text}</p>}
          </div>
        </div>
      </div>
    </Card>
  );
}

export function YourComputers() {
  const { t } = useTranslation('bv');
  const { devices, error, refresh } = useDevices();
  return (
    <section id="computers" className="bv-computers">
      <div className="bv-section-head"><h2 className="bv-section-title">{t('computers.title')}</h2></div>
      {error && <Card><p className="bv-error-text">{error}</p></Card>}
      {!devices && !error && <Card><Skeleton height={60} /></Card>}
      {devices && devices.length === 0 && (
        <Card><EmptyState icon="print" title={t('computers.empty.title')}>{t('computers.empty.body')}</EmptyState></Card>
      )}
      <div className="bv-computers__list">
        {devices && devices.map((d) => <ComputerCard key={d.id} d={d} onChanged={refresh} />)}
      </div>
      <ConnectComputer onPaired={refresh} />
    </section>
  );
}

